import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable } from 'rxjs';
import { filter, map, take, finalize } from 'rxjs/operators';
import { DateRange } from '@angular/material/datepicker';

import {
  AddReservationRequestGQL,
  Booking,
  Franchise, GetAvailableItemsGQL, GetAvailableItemsQuery,
  HasExistingReservationsGQL,
  ReservationRequestInput,
  ReservationServiceInput,
  Item,
  ItemCategory,
  ItemType,
} from '../graphql.service';

import { ReservationServiceCategory } from '../../models/reservation/reservation-service-category.model';
import { CategoriesWithRules } from '../../models/reservation/reservation-types';
import { ReservationServiceItem } from '../../models/reservation/reservation-service.model';
import { ReservationRequestItem } from 'src/app/models/reservation/reservation-request.model';
import { ReservationPetInfo } from 'src/app/models/reservation-pet-info.model';

import * as lodash from 'lodash';

@Injectable({
  providedIn: 'root',
})
export class ReservationRequestService {

  // #region -> Property - Reservation Request <-
  // ----------

  reservationRequest: ReservationRequestItem = new ReservationRequestItem({});
  reservationRequest$ = new BehaviorSubject<ReservationRequestItem>(this.reservationRequest);

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Property - Services <-
  // ----------

  availableServiceCategories: { [id: string]: ReservationServiceCategory } = {};
  availableServiceCategories$ = new BehaviorSubject<{ [id: string]: ReservationServiceCategory }>({});

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Constructor <-
  // ----------

  constructor(
    private getAvailableItemsQuery: GetAvailableItemsGQL,
    private hasExistingReservationQuery: HasExistingReservationsGQL,
    private addReservationRequestMutation: AddReservationRequestGQL
  ) {}

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Method - Change Detection <-
  // ----------

  /**
   * Check if need to warn user about the location change.
   *
   * There is only "1" case that we need to warn the user about the location change.
   * - It already has the "selection" in the "reservation request", and the new selection is different.
   *
   * @param location The new location
   */
  locationChangeWarning(location: Franchise): boolean {
    //
    if (this.reservationRequest.selectedLocation && this.reservationRequest.selectedLocation.id !== location.id) {
      // No change
      return true;
    }

    //
    return false;
  }

  petChangeWarning(pets: ReservationPetInfo[]): boolean {
    //
    return this.reservationRequest.isPetSelectionChanged(pets);
  }

  serviceChangeWarning(newReservationRequest: ReservationRequestItem): boolean {
    //
    return this.reservationRequest.isServiceSelectionChanged(newReservationRequest);
  }

  dateChangeWarning(newDateRange: DateRange<any>): boolean {
    // Init - no warning
    if (!this.reservationRequest.selectedDateRange) {
      return false;
    }

    return this.reservationRequest.isDateSelectionChanged(newDateRange);
  }

  addonChangeWarning(newReservationRequest: ReservationRequestItem): boolean {
    //
    return this.reservationRequest.isAddonSelectionChanged(newReservationRequest);
  }

  // ----------
  // #endregion

  // ///////////////////////////

  // #region -> Method - Updates <-
  // ----------

  /**
   * Update the location selection.
   *
   * By changing the location, the following info to be reset:
   * - Selected pets
   * - Core services
   * - Selected date(s)
   * - Add-on services
   * - Notes
   *
   */
  updateSelectedLocation(location: Franchise): void {
    //
    this.reservationRequest = this.reservationRequest.update({
      selectedLocation: lodash.cloneDeep(location),
      selectedPets: [],
      coreServices: [],
      coreCategoryIds: [],
      selectedDateRange: undefined,
      addonServices: {},
      addonCategoryIds: {},
      reservationNotes: undefined,
    });
    this.reservationRequest$.next(this.reservationRequest.clone());
  }

  /**
   * Update the pet(s) selection.
   *
   * By changing the pet(s), the following info to be reset:
   * - Core services
   * - Selected date(s)
   * - Add-on services
   * - Notes
   *
   */
  updateSelectedPets(pets: ReservationPetInfo[]): void {
    //
    this.reservationRequest = this.reservationRequest.update({
      selectedPets: lodash.cloneDeep(pets),
      coreServices: [],
      coreCategoryIds: [],
      selectedDateRange: undefined,
      addonServices: {},
      addonCategoryIds: {},
      reservationNotes: undefined,
    });
    this.reservationRequest$.next(this.reservationRequest.clone());
  }

  /**
   * Update the pet(s) selection.
   *
   * By changing the service(s), the following info to be reset:
   * - Selected date(s)
   * - Add-on services
   * - Notes
   *
   */
  updateSelectedServices(newRequest: ReservationRequestItem): void {
    //
    this.reservationRequest = this.reservationRequest.update({
      selectedLocation: newRequest.selectedLocation,
      selectedPets: newRequest.selectedPets,
      coreServices: newRequest.coreServices,
      coreCategoryIds: newRequest.coreCategoryIds,
      selectedDateRange: undefined,
      addonServices: {},
      addonCategoryIds: {},
      // Keep the prev. notes
      reservationNotes: newRequest.reservationNotes,
    });
    this.reservationRequest$.next(this.reservationRequest.clone());
  }

  /**
   * Update the date(s) selection.
   */
  updateSelectedDateRange(dateRange: DateRange<any>): void {
    //
    this.reservationRequest = this.reservationRequest.update({
      selectedDateRange: dateRange,
    });
    this.reservationRequest$.next(this.reservationRequest.clone());
  }

  updateReservationRequestItem(newRequest: ReservationRequestItem): void {
    //
    this.reservationRequest = newRequest.clone();
    this.reservationRequest$.next(this.reservationRequest.clone());
  }

  hasBoardingServiceSelected(): boolean {
    return this.reservationRequest.includeCoreCategory(CategoriesWithRules.Boarding);
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Method - Service  <-
  // ----------

  fetchAvailableServices(locationId: string): Observable<{
    [id: string]: ReservationServiceCategory;
  }> {
    // Reset
    this.availableServiceCategories = {};

    return this.getAvailableItemsQuery.fetch({ locationId }).pipe(
      take(1),
      map((res) => {
        //
        if (res.data) {
          //
          res.data.getAvailableItems.forEach((item) => {
            // Only load "active" services
            const itemCategory = item.category as ItemCategory;
            const itemType = item.type as ItemType;
            if (itemType.label === 'Services' && itemCategory.active) {
              // Create a new "service"
              const newService = new ReservationServiceItem({
                linkedService: item as Item,
              });

              // Check if needed to create a new "category"
              if (
                !lodash.has(this.availableServiceCategories, itemCategory.id)
              ) {
                // Create
                const newCategory = new ReservationServiceCategory({
                  categoryId: itemCategory.id,
                  categoryName: itemCategory.label,
                });
                this.availableServiceCategories[newCategory.categoryId] =
                  lodash.cloneDeep(newCategory);
              }

              // Get linked category
              const linkedCategory: ReservationServiceCategory =
                this.availableServiceCategories[itemCategory.id];

              // Update category info to service
              newService.updateCategoryInfo(linkedCategory);

              // Add to category
              linkedCategory.addNewService(newService.clone());
            }
          });
        }

        //
        this.availableServiceCategories = {...this.availableServiceCategories};
        this.availableServiceCategories$.next(this.availableServiceCategories);
        return this.availableServiceCategories;
      })
    );
  }

  checkConflict(dateRange: DateRange<any>): Observable<Booking[][]> {
    //
    const checkConflictCallingList: Observable<Booking[]>[] = [];
    this.reservationRequest.coreServices.forEach((service) => {
      //
      this.reservationRequest.selectedPets.forEach((pet) => {
        //
        checkConflictCallingList.push(
          this.hasExistingReservationQuery
            .fetch({
              reservationService: {
                startDate: dateRange.start,
                endDate: dateRange.end ? dateRange.end : dateRange.start,
                petId: pet.id,
                serviceItemId: service.serviceId,
              },
            })
            .pipe(map((res) => res.data.hasExistingReservations))
        );
      });
    });

    //
    return forkJoin(checkConflictCallingList);
  }

  /**
   * If it needs "date range" to cover the current selection of services.
   *
   * Rules:
   * - If the "core service" includes "Boarding"
   */
  serviceNeedsDateRange(): boolean {
    //
    return this.reservationRequest.coreCategoryIds.includes(
      CategoriesWithRules.Boarding
    );
  }

  reset(): void {
    this.reservationRequest = new ReservationRequestItem({});
    this.reservationRequest$.next(this.reservationRequest.clone());
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Method - Submission <-
  // ----------

  submitReservationRequest(): Observable<any> {
    //
    const reservationInput = this._prepReservationRequest();

    //
    return this.addReservationRequestMutation
      .mutate({
        reservationInput,
      })
      .pipe(map((res) => res.data.addReservationRequest));
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Private Method <-
  // ----------

  private _prepReservationRequest(): ReservationRequestInput {
    //
    const franchiseId = this.reservationRequest.selectedLocation.id;
    const notes = this.reservationRequest.reservationNotes || '';

    const dateRange = this.reservationRequest.selectedDateRange;
    const startDate = dateRange.start.utc().format('YYYY-MM-DD');
    let endDate = dateRange.end;
    if (endDate) {
      endDate = endDate.utc().format('YYYY-MM-DD');
    } else {
      // Use "start date"
      endDate = startDate;
    }

    // Build the "services"
    const services: ReservationServiceInput[] = [];
    this.reservationRequest.selectedPets.forEach((pet) => {
      const petId = pet.id;
      // Core services
      this.reservationRequest.coreServices.forEach((service) => {
        const serviceItemId = service.serviceId;
        /*
          For all "non-boarding" service, set the `start date` to the `end date`
            to help void the incorrect calculation of `qty` of the service item
            based on the current calculation logic of DD portal.
        */
        if (service.serviceCategoryId === CategoriesWithRules.Boarding) {
          services.push({ petId, startDate, endDate, serviceItemId });
        } else {
          // Set the `start date` to the value of `end date`
          services.push({ petId, startDate: endDate, endDate, serviceItemId });
        }
      });

      // Addon services
      if (this.reservationRequest.addonServices.hasOwnProperty(petId)) {
        const petAddonServices = this.reservationRequest.addonServices[petId];
        petAddonServices.forEach((service) => {
          const serviceItemId = service.serviceId;

          /*
            If a service has quantity, just duplicate the entries for "#" times
          */
          if (service.serviceQuantity > 0) {
            lodash.times(service.serviceQuantity, () => {
              // Use "end date" as the "start date" to avoid the multiple `qty` on the backend
              services.push({ petId, startDate: endDate, endDate, serviceItemId });
            });
          }
        });
      }
    });

    //
    return { franchiseId, notes, services };
  }

  // ----------
  // #endregion

  /////////////////////////////
}
